import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/app/AppShell';
import { confirm } from '@/components/ui/ConfirmDialog';
import { useCurrentUser } from '@/lib/auth';
import { useMyCompany, useUpdateMyCompany } from '@/lib/companies';
import { useAuditLog } from '@/lib/auditLog';
import { useEmployees } from '@/lib/employees';
import { useStartCheckout, useStripePortal, useSubscription, usePlans, type PaidTier, type PlanCatalogEntry, type StripeInterval } from '@/lib/billing';
import { FREE_SEAT_LIMIT, PLAN_LABELS, type PlanCode } from '@/lib/plans';
import { useApiTokens, useCreateApiToken, useRevokeApiToken, type ApiToken } from '@/lib/api-tokens';
import { useWebhooks, useCreateWebhook, useDisableWebhook, WEBHOOK_EVENTS, type Webhook } from '@/lib/webhooks';
import { SsoTab } from '@/components/app/SsoTab';
import { useMembers, useInviteMember, useRevokeInvite, useUpdateMemberRole, useRemoveMember, type MemberRole } from '@/lib/members';

const TABS = ['Workspace', 'Members', 'SSO', 'API tokens', 'Webhooks', 'Billing', 'Audit log'] as const;
type Tab = typeof TABS[number];

const TAB_KEYS: Record<Tab, string> = {
  'Workspace': 'tabWorkspace',
  'Members': 'tabMembers',
  'SSO': 'tabSso',
  'API tokens': 'tabApiTokens',
  'Webhooks': 'tabWebhooks',
  'Billing': 'tabBilling',
  'Audit log': 'tabAuditLog',
};

export function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('Workspace');
  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('settings.title', { defaultValue: 'Settings' })}</h1>

        <div className="mt-6 flex items-center gap-2 border-b border-ink-200">
          {TABS.map((tabName) => (
            <button
              key={tabName}
              onClick={() => setTab(tabName)}
              className={`px-4 py-2 text-sm font-semibold transition border-b-2 -mb-px ${tab === tabName ? 'border-accent-500 text-accent-700' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
            >
              {t(`settings.${TAB_KEYS[tabName]}`, { defaultValue: tabName })}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'Workspace' && <WorkspaceTab />}
          {tab === 'Members' && <MembersTab />}
          {tab === 'SSO' && <SsoTab />}
          {tab === 'API tokens' && <ApiTokensTab />}
          {tab === 'Webhooks' && <WebhooksTab />}
          {tab === 'Billing' && <BillingTab />}
          {tab === 'Audit log' && <AuditLogTab />}
        </div>
      </div>
    </AppShell>
  );
}

const MEMBER_ROLES: MemberRole[] = ['admin', 'manager', 'member'];

function MembersTab() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const data = useMembers();
  const invite = useInviteMember();
  const revoke = useRevokeInvite();
  const updateRole = useUpdateMemberRole();
  const remove = useRemoveMember();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [error, setError] = useState<string | null>(null);

  const isOwner = me.data?.role === 'owner';
  const canManage = isOwner || me.data?.role === 'admin';

  async function sendInvite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await invite.mutateAsync({ email: email.trim(), role });
      setEmail('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || (err as Error).message));
    }
  }

  if (!canManage) {
    return <p className="text-sm text-ink-500">{t('settings.membersOnlyAdmins', { defaultValue: 'Only owners and admins can manage workspace members.' })}</p>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <form onSubmit={sendInvite} className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-ink-900">{t('settings.inviteTeammate', { defaultValue: 'Invite a teammate' })}</h2>
        <p className="text-xs text-ink-500 mt-0.5">{t('settings.inviteTeammateDesc', { defaultValue: 'They’ll get an email to set a password and join this workspace.' })}</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[220px] block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('settings.email', { defaultValue: 'Email' })}</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('settings.emailPlaceholder', { defaultValue: 'colleague@company.com' })}
              className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-ink-700 mb-1">{t('settings.role', { defaultValue: 'Role' })}</span>
            <select value={role} onChange={(e) => setRole(e.target.value as MemberRole)}
              className="border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none">
              {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button type="submit" disabled={invite.isPending || !email.trim()}
            className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
            {invite.isPending ? t('settings.sending', { defaultValue: 'Sending…' }) : t('settings.sendInvite', { defaultValue: 'Send invite' })}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
      </form>

      <div className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-100 bg-ink-50"><h2 className="text-sm font-semibold text-ink-700">{t('settings.members', { defaultValue: 'Members' })}</h2></div>
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-ink-100">
            {(data.data?.members ?? []).map((m) => (
              <tr key={m.id} className="hover:bg-ink-50/40">
                <td className="px-5 py-3">
                  <p className="font-medium text-ink-900">{m.displayName}{m.isYou && <span className="text-ink-400 font-normal"> {t('settings.youSuffix', { defaultValue: '(you)' })}</span>}</p>
                  <p className="text-[11px] text-ink-500">{m.email}</p>
                </td>
                <td className="px-5 py-3">
                  {isOwner && m.role !== 'owner' && !m.isYou ? (
                    <select value={m.role} onChange={(e) => updateRole.mutate({ userId: m.id, role: e.target.value as MemberRole })}
                      className="border border-ink-300 rounded-lg px-2 py-1 text-xs focus:border-accent-500 focus:outline-none">
                      {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-500">{m.role}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-end">
                  {m.role !== 'owner' && !m.isYou && (
                    <button onClick={() => remove.mutate(m.id)} className="text-rose-600 hover:text-rose-500 text-xs font-semibold">{t('settings.remove', { defaultValue: 'Remove' })}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(data.data?.invites ?? []).length > 0 && (
        <div className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 bg-ink-50"><h2 className="text-sm font-semibold text-ink-700">{t('settings.pendingInvites', { defaultValue: 'Pending invites' })}</h2></div>
          <ul className="divide-y divide-ink-100">
            {(data.data?.invites ?? []).map((i) => (
              <li key={i.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-ink-900">{i.email}</p>
                  <p className="text-[11px] text-ink-500">{t('settings.inviteMeta', { defaultValue: '{{role}} · invited {{date}}', role: i.role, date: new Date(i.createdAt).toLocaleDateString() })}</p>
                </div>
                <button onClick={() => revoke.mutate(i.id)} className="text-rose-600 hover:text-rose-500 text-xs font-semibold">{t('settings.revoke', { defaultValue: 'Revoke' })}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const PLAN_BADGE_CLASS: Record<PlanCode, string> = {
  free:       'bg-ink-100 text-ink-700',
  growth:     'bg-emerald-100 text-emerald-800',
  global:     'bg-sky-100 text-sky-800',
  enterprise: 'bg-violet-100 text-violet-800',
};

function PlanBadge({ plan }: { plan: PlanCode | string | null | undefined }) {
  const { t } = useTranslation();
  const norm = (((plan ?? 'free') as string).toLowerCase()) as PlanCode;
  const className = PLAN_BADGE_CLASS[norm] ?? PLAN_BADGE_CLASS.free;
  const label = PLAN_LABELS[norm] ?? t('settings.planFree', { defaultValue: 'Free' });
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
}

function WorkspaceTab() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const company = useMyCompany();
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-6">
      <h2 className="text-base font-semibold text-ink-900">{t('settings.workspace', { defaultValue: 'Workspace' })}</h2>
      <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label={t('settings.companyName', { defaultValue: 'Company name' })} value={company.data?.name} />
        <Field label={t('settings.hqCountry', { defaultValue: 'HQ country' })} value={company.data?.hqCountryCode} />
        <FieldNode label={t('settings.plan', { defaultValue: 'Plan' })}>
          <PlanBadge plan={company.data?.plan} />
        </FieldNode>
        <Field label={t('settings.reportingCurrency', { defaultValue: 'Reporting currency' })} value={company.data?.reportingCurrency} />
        <Field label={t('settings.fiscalYearStart', { defaultValue: 'Fiscal year start' })} value={new Date(2000, (company.data?.fiscalYearStartMonth ?? 1) - 1, 1).toLocaleString('en', { month: 'long' })} />
        <Field label={t('settings.verification', { defaultValue: 'Verification' })} value={company.data?.verificationStatus} />
        <Field label={t('settings.countries', { defaultValue: 'Countries' })} value={(company.data?.countries ?? []).join(', ')} />
        <Field label={t('settings.you', { defaultValue: 'You' })} value={`${user.data?.displayName} (${user.data?.role})`} />
        <Field label={t('settings.email', { defaultValue: 'Email' })} value={user.data?.email} />
      </dl>
      <IndustrySelector
        current={company.data?.industry ?? null}
        canEdit={user.data?.role === 'owner' || user.data?.role === 'admin'}
      />
    </div>
  );
}

const INDUSTRY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'general', label: 'General / Office (一般)' },
  { value: 'software_information', label: 'Software & Information (情報通信)' },
  { value: 'finance_insurance_realestate', label: 'Finance / Insurance / Real estate (金融・保険・不動産)' },
  { value: 'construction', label: 'Construction (建設)' },
];

/**
 * Company industry — drives JP 雇用保険 + 労災 rates. If left unset the payroll
 * engine falls back to "general", which is wrong for construction etc., so this
 * makes it explicit + editable instead of a silent default.
 */
function IndustrySelector({ current, canEdit }: { current: string | null; canEdit: boolean }) {
  const { t } = useTranslation();
  const update = useUpdateMyCompany();
  const [value, setValue] = useState(current ?? 'general');
  useEffect(() => { setValue(current ?? 'general'); }, [current]);
  return (
    <div className="mt-5 pt-4 border-t border-ink-100">
      <label className="block text-sm font-semibold text-ink-900">{t('settings.industry', { defaultValue: 'Industry' })}</label>
      <p className="text-xs text-ink-500 mt-0.5 mb-2">{t('settings.industryDesc', { defaultValue: 'Determines JP 雇用保険 (employment insurance) & 労災 (workers\' comp) rates. Set this to avoid the "general" default.' })}</p>
      <div className="flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit}
          className="h-10 rounded-lg border border-ink-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/20 disabled:opacity-60"
        >
          {INDUSTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canEdit && (
          <button
            onClick={() => update.mutate({ industry: value })}
            disabled={update.isPending || value === (current ?? 'general')}
            className="h-10 rounded-lg bg-accent-500 text-white px-4 text-sm font-semibold hover:bg-accent-600 disabled:opacity-50"
          >
            {update.isPending ? t('settings.saving', { defaultValue: 'Saving…' }) : t('settings.save', { defaultValue: 'Save' })}
          </button>
        )}
      </div>
      {!current && <p className="text-[11px] text-amber-600 mt-1.5">{t('settings.industryNotSet', { defaultValue: '⚠ Not set — payroll currently uses the "general" rate.' })}</p>}
    </div>
  );
}

/**
 * Workspace API tokens. KeenEZ's REST API is the same endpoints
 * the browser hits — every controller already accepts a Bearer
 * `kez_…` token in place of the session cookie. Tokens act as
 * the workspace owner; they're hashed at rest and can be revoked
 * any time.
 *
 * The raw token is shown exactly once on creation. After that
 * only the prefix + name + last-used timestamp are visible.
 */
function ApiTokensTab() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const tokens = useApiTokens();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [justCreated, setJustCreated] = useState<ApiToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = user.data?.role;
  const canManage = role === 'owner' || role === 'admin';

  async function submit() {
    setError(null);
    try {
      const tok = await create.mutateAsync(newName.trim());
      setJustCreated(tok);
      setCreating(false);
      setNewName('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || t('settings.tokenCreateFailed', { defaultValue: 'Failed to create token' });
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  async function onRevoke(id: string) {
    const ok = await confirm({
      title: t('settings.revokeTokenTitle', { defaultValue: 'Revoke this token?' }),
      description: t('settings.revokeTokenDesc', { defaultValue: 'Any integration using it will start getting 401s immediately.' }),
      tone: 'destructive',
      confirmLabel: t('settings.revoke', { defaultValue: 'Revoke' }),
    });
    if (!ok) return;
    try { await revoke.mutateAsync(id); } catch { /* noop, list reflects state */ }
  }

  async function copyToken(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unsupported */ }
  }

  const liveTokens = (tokens.data ?? []).filter((tok) => !tok.revokedAt);
  const revokedTokens = (tokens.data ?? []).filter((tok) => tok.revokedAt);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink-900">{t('settings.apiTokens', { defaultValue: 'API tokens' })}</h2>
            <p className="mt-1 text-xs text-ink-600 max-w-2xl">
              {t('settings.apiTokensIntro', { defaultValue: 'Talk to the KeenEZ REST API from outside the browser. Each token authenticates as the workspace owner. Use the' })} <code className="font-mono bg-ink-100 px-1 py-0.5 rounded">Authorization: Bearer kez_…</code> {t('settings.apiTokensIntroMid', { defaultValue: 'header on any endpoint. See' })} <a href="/api-docs" className="text-accent-700 underline">{t('settings.apiReferenceLink', { defaultValue: 'the API reference' })}</a>.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => { setCreating(true); setError(null); }}
              className="shrink-0 inline-flex items-center px-3 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 transition"
            >
              {t('settings.newToken', { defaultValue: '+ New token' })}
            </button>
          )}
        </div>

        {justCreated?.token && (
          <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/70 p-4">
            <p className="text-sm font-semibold text-emerald-900">
              {t('settings.tokenCreated', { defaultValue: 'Token created — copy it now.' })}
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              {t('settings.tokenCreatedDesc', { defaultValue: "This is the only time we'll show the full secret. Store it somewhere safe." })}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate font-mono text-xs bg-white border border-emerald-200 rounded-md px-3 py-2">{justCreated.token}</code>
              <button
                type="button"
                onClick={() => copyToken(justCreated.token!)}
                className="shrink-0 inline-flex items-center px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
              >
                {copied ? t('settings.copied', { defaultValue: 'Copied ✓' }) : t('settings.copy', { defaultValue: 'Copy' })}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setJustCreated(null)}
              className="mt-3 text-xs text-emerald-700 hover:text-emerald-900"
            >
              {t('settings.dismiss', { defaultValue: 'Dismiss' })}
            </button>
          </div>
        )}

        {creating && (
          <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/50 p-4">
            <label className="block">
              <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('settings.tokenName', { defaultValue: 'Token name' })}</span>
              <input
                type="text"
                autoFocus
                placeholder={t('settings.tokenNamePlaceholder', { defaultValue: 'e.g. n8n workflow, BI export, mobile app' })}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim().length >= 2) submit(); }}
                className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
              />
            </label>
            {error && (
              <p className="mt-2 text-xs text-rose-700">{error}</p>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setCreating(false); setError(null); setNewName(''); }}
                className="px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-100 rounded-md"
              >
                {t('settings.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={create.isPending || newName.trim().length < 2}
                className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50 transition"
              >
                {create.isPending ? t('settings.generating', { defaultValue: 'Generating…' }) : t('settings.generateToken', { defaultValue: 'Generate token' })}
              </button>
            </div>
          </div>
        )}

        <div className="mt-5">
          {tokens.isLoading && <p className="text-xs text-ink-500">{t('settings.loading', { defaultValue: 'Loading…' })}</p>}
          {!tokens.isLoading && liveTokens.length === 0 && (
            <p className="text-xs text-ink-500 italic">{t('settings.noTokens', { defaultValue: 'No tokens yet. Create one to start using the REST API.' })}</p>
          )}
          {liveTokens.length > 0 && (
            <ul className="divide-y divide-ink-100 border border-ink-200 rounded-xl overflow-hidden">
              {liveTokens.map((tok) => (
                <li key={tok.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-900 truncate">{tok.name}</p>
                    <p className="text-[11px] text-ink-500 font-mono">{tok.tokenPrefix}…{'•'.repeat(36)}</p>
                    <p className="text-[10px] text-ink-400 mt-0.5">
                      {tok.lastUsedAt
                        ? t('settings.tokenMetaUsed', { defaultValue: 'Created {{created}} · last used {{lastUsed}}', created: new Date(tok.createdAt).toLocaleDateString(), lastUsed: new Date(tok.lastUsedAt).toLocaleString() })
                        : t('settings.tokenMetaNeverUsed', { defaultValue: 'Created {{created}} · never used', created: new Date(tok.createdAt).toLocaleDateString() })}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => onRevoke(tok.id)}
                      className="shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold text-rose-700 hover:bg-rose-50 transition"
                    >
                      {t('settings.revoke', { defaultValue: 'Revoke' })}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {revokedTokens.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-ink-500 cursor-pointer">{t('settings.revokedCount', { defaultValue: 'Revoked ({{count}})', count: revokedTokens.length })}</summary>
            <ul className="mt-2 space-y-1">
              {revokedTokens.map((tok) => (
                <li key={tok.id} className="px-3 py-2 rounded-md bg-ink-50 text-xs text-ink-500 line-through">
                  {t('settings.revokedTokenLine', { defaultValue: '{{name}} · {{prefix}}… · revoked {{date}}', name: tok.name, prefix: tok.tokenPrefix, date: tok.revokedAt ? new Date(tok.revokedAt).toLocaleDateString() : '' })}
                </li>
              ))}
            </ul>
          </details>
        )}

        {!canManage && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {t('settings.tokensOnlyAdmins', { defaultValue: 'Only workspace owners and admins can create or revoke API tokens.' })}
          </p>
        )}
      </div>

      {/* Public REST API (v1) quick reference. */}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <h2 className="text-base font-semibold text-ink-900">{t('settings.publicRestApi', { defaultValue: 'Public REST API' })} <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-700 bg-accent-50 rounded px-1.5 py-0.5 align-middle">v1</span></h2>
        <p className="mt-1 text-xs text-ink-600 max-w-2xl">
          {t('settings.publicRestApiIntro', { defaultValue: 'Read-only, PII-free endpoints scoped to your workspace — for HRIS sync, BI tools, and internal scripts. Authenticate with any active token above via' })} <code className="font-mono bg-ink-100 px-1 py-0.5 rounded">Authorization: Bearer kez_…</code> {t('settings.or', { defaultValue: 'or' })} <code className="font-mono bg-ink-100 px-1 py-0.5 rounded">X-API-Key</code>.
        </p>
        <ul className="mt-3 space-y-1 text-xs font-mono text-ink-700">
          <li><span className="text-emerald-600 font-semibold">GET</span> /api/v1/me</li>
          <li><span className="text-emerald-600 font-semibold">GET</span> /api/v1/employees<span className="text-ink-400"> ?status&country&limit&offset</span></li>
          <li><span className="text-emerald-600 font-semibold">GET</span> /api/v1/employees/:id</li>
          <li><span className="text-emerald-600 font-semibold">GET</span> /api/v1/departments</li>
          <li><span className="text-emerald-600 font-semibold">GET</span> /api/v1/payroll/runs<span className="text-ink-400"> ?limit</span></li>
        </ul>
        <pre className="mt-3 rounded-lg bg-ink-900 text-ink-50 text-[11px] p-3 overflow-x-auto"><code>curl -H "Authorization: Bearer kez_…" \
  {(import.meta.env.VITE_API_URL as string | undefined) || 'https://api.keenez.com'}/api/v1/employees</code></pre>
        <p className="mt-2 text-[11px] text-ink-500">{t('settings.realtimeEvents', { defaultValue: 'Real-time events? Subscribe under' })} <span className="font-semibold">{t('settings.tabWebhooks', { defaultValue: 'Webhooks' })}</span>. {t('settings.fullReference', { defaultValue: 'Full reference:' })} <a href="/api-docs" className="text-accent-700 underline">{t('settings.apiDocsLink', { defaultValue: 'API docs' })}</a>.</p>
      </div>
    </div>
  );
}

/**
 * Webhook subscriptions. Receivers verify deliveries via HMAC-SHA256
 * over the body using the secret we show once on creation. Failed
 * deliveries get logged into the row (last_delivery_status +
 * last_delivery_error) but don't auto-retry in v1; a back-pressured
 * retry queue lands in v1.1.
 */
function WebhooksTab() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const webhooks = useWebhooks();
  const create = useCreateWebhook();
  const disable = useDisableWebhook();
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [justCreated, setJustCreated] = useState<Webhook | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = user.data?.role;
  const canManage = role === 'owner' || role === 'admin';

  async function submit() {
    setError(null);
    try {
      const w = await create.mutateAsync({
        url: url.trim(),
        events,
        description: description.trim() || undefined,
      });
      setJustCreated(w);
      setCreating(false);
      setUrl(''); setEvents([]); setDescription('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || t('settings.webhookCreateFailed', { defaultValue: 'Failed to create webhook' });
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  async function copy(s: string) {
    try {
      await navigator.clipboard.writeText(s);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unsupported */ }
  }

  const live = (webhooks.data ?? []).filter((w) => !w.disabledAt);
  const disabled = (webhooks.data ?? []).filter((w) => w.disabledAt);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink-900">{t('settings.webhooks', { defaultValue: 'Webhooks' })}</h2>
            <p className="mt-1 text-xs text-ink-600 max-w-2xl">
              {t('settings.webhooksIntro', { defaultValue: 'Receive a POST to your URL whenever something happens in this workspace — employee changes, payroll runs, signed documents, completed approvals. Verify the' })} <code className="font-mono bg-ink-100 px-1 py-0.5 rounded">X-KeenEZ-Signature</code> {t('settings.webhooksIntroMid', { defaultValue: 'header against the secret we show once on creation.' })}
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => { setCreating(true); setError(null); }}
              className="shrink-0 inline-flex items-center px-3 py-2 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 transition"
            >
              {t('settings.addWebhook', { defaultValue: '+ Add webhook' })}
            </button>
          )}
        </div>

        {justCreated?.secret && (
          <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/70 p-4">
            <p className="text-sm font-semibold text-emerald-900">{t('settings.webhookSaved', { defaultValue: 'Webhook saved — copy the signing secret now.' })}</p>
            <p className="mt-1 text-xs text-emerald-800">{t('settings.webhookSavedDesc', { defaultValue: 'Verify each delivery by computing HMAC-SHA256(body, secret) and comparing the hex digest to the' })} <code className="font-mono bg-white border border-emerald-200 px-1 rounded">X-KeenEZ-Signature: sha256=&hellip;</code> {t('settings.webhookSavedDescEnd', { defaultValue: 'header.' })}</p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate font-mono text-xs bg-white border border-emerald-200 rounded-md px-3 py-2">{justCreated.secret}</code>
              <button type="button" onClick={() => copy(justCreated.secret!)} className="shrink-0 inline-flex items-center px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition">{copied ? t('settings.copied', { defaultValue: 'Copied ✓' }) : t('settings.copy', { defaultValue: 'Copy' })}</button>
            </div>
            <button type="button" onClick={() => setJustCreated(null)} className="mt-3 text-xs text-emerald-700 hover:text-emerald-900">{t('settings.dismiss', { defaultValue: 'Dismiss' })}</button>
          </div>
        )}

        {creating && (
          <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/50 p-4 space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('settings.targetUrl', { defaultValue: 'Target URL' })}</span>
              <input
                type="url"
                autoFocus
                placeholder={t('settings.targetUrlPlaceholder', { defaultValue: 'https://your-app.example.com/webhooks/keenez' })}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
              />
            </label>
            <div>
              <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('settings.events', { defaultValue: 'Events' })}</span>
              <p className="text-[10px] text-ink-500 mb-2">{t('settings.eventsHint', { defaultValue: 'Leave empty to subscribe to every event.' })}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {WEBHOOK_EVENTS.map((ev) => {
                  const checked = events.includes(ev);
                  return (
                    <label key={ev} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-xs cursor-pointer transition ${checked ? 'border-accent-500 bg-accent-50 text-accent-900' : 'border-ink-200 hover:bg-ink-50'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))}
                        className="accent-accent-500"
                      />
                      <span className="font-mono">{ev}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-ink-700 mb-1.5">{t('settings.descriptionOptional', { defaultValue: 'Description (optional)' })}</span>
              <input
                type="text"
                placeholder={t('settings.descriptionPlaceholder', { defaultValue: 'e.g. Notify Slack on payroll runs' })}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
              />
            </label>
            {error && <p className="text-xs text-rose-700">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setCreating(false); setUrl(''); setEvents([]); setDescription(''); }} className="px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-100 rounded-md">{t('settings.cancel', { defaultValue: 'Cancel' })}</button>
              <button type="button" onClick={submit} disabled={create.isPending || url.trim().length < 8} className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
                {create.isPending ? t('settings.savingWebhook', { defaultValue: 'Saving…' }) : t('settings.saveWebhook', { defaultValue: 'Save webhook' })}
              </button>
            </div>
          </div>
        )}

        <div className="mt-5">
          {webhooks.isLoading && <p className="text-xs text-ink-500">{t('settings.loading', { defaultValue: 'Loading…' })}</p>}
          {!webhooks.isLoading && live.length === 0 && (
            <p className="text-xs text-ink-500 italic">{t('settings.noWebhooks', { defaultValue: 'No webhooks yet.' })}</p>
          )}
          {live.length > 0 && (
            <ul className="divide-y divide-ink-100 border border-ink-200 rounded-xl overflow-hidden">
              {live.map((w) => (
                <li key={w.id} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-900 truncate">{w.url}</p>
                    {w.description && <p className="text-[11px] text-ink-500 mt-0.5">{w.description}</p>}
                    <p className="text-[10px] text-ink-500 mt-1 font-mono">
                      {w.events.length === 0 ? t('settings.everyEvent', { defaultValue: 'every event' }) : w.events.join(' · ')}
                    </p>
                    <p className="text-[10px] mt-0.5">
                      {w.lastDeliveryAt ? (
                        <>
                          <span className={w.lastDeliveryStatus && w.lastDeliveryStatus >= 200 && w.lastDeliveryStatus < 300 ? 'text-emerald-700' : 'text-rose-700'}>
                            {t('settings.lastDelivery', { defaultValue: 'Last: {{status}}{{error}}', status: w.lastDeliveryStatus ?? '—', error: w.lastDeliveryError ? ` (${w.lastDeliveryError})` : '' })}
                          </span>
                          <span className="text-ink-400"> · {new Date(w.lastDeliveryAt).toLocaleString()}</span>
                        </>
                      ) : (
                        <span className="text-ink-400">{t('settings.neverDelivered', { defaultValue: 'Never delivered' })}</span>
                      )}
                    </p>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => disable.mutate(w.id)}
                      className="shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold text-rose-700 hover:bg-rose-50 transition"
                    >
                      {t('settings.disable', { defaultValue: 'Disable' })}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {disabled.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-ink-500 cursor-pointer">{t('settings.disabledCount', { defaultValue: 'Disabled ({{count}})', count: disabled.length })}</summary>
            <ul className="mt-2 space-y-1">
              {disabled.map((w) => (
                <li key={w.id} className="px-3 py-2 rounded-md bg-ink-50 text-xs text-ink-500 line-through truncate">
                  {t('settings.disabledWebhookLine', { defaultValue: '{{url}} · disabled {{date}}', url: w.url, date: w.disabledAt ? new Date(w.disabledAt).toLocaleDateString() : '' })}
                </li>
              ))}
            </ul>
          </details>
        )}

        {!canManage && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            {t('settings.webhooksOnlyAdmins', { defaultValue: 'Only workspace owners and admins can manage webhooks.' })}
          </p>
        )}
      </div>
    </div>
  );
}

function fmtBillingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function BillingTab() {
  const { t } = useTranslation();
  const company = useMyCompany();
  const employees = useEmployees();
  const portal = useStripePortal();
  const subscription = useSubscription();
  const checkout = useStartCheckout();
  const plansQ = usePlans();
  const [interval, setInterval] = useState<StripeInterval>('monthly');
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingBanner, setBillingBanner] = useState<'success' | 'cancelled' | null>(null);
  const qc = useQueryClient();

  // Stripe return handling — banner + refresh, then strip the param.
  useEffect(() => {
    const status = searchParams.get('billing');
    if (status === 'success' || status === 'cancelled') {
      setBillingBanner(status);
      if (status === 'success') {
        qc.invalidateQueries({ queryKey: ['company', 'me'] });
        qc.invalidateQueries({ queryKey: ['billing-subscription'] });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('billing');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize the monthly/yearly toggle to the user's ACTUAL billing cycle
  // once the subscription loads (then let them change it freely).
  const cycleSynced = useRef(false);
  useEffect(() => {
    if (!cycleSynced.current && subscription.data?.interval) {
      setInterval(subscription.data.interval);
      cycleSynced.current = true;
    }
  }, [subscription.data?.interval]);

  const plan = ((company.data?.plan ?? 'free') as string).toLowerCase() as PlanCode;
  const seatCount = employees.data?.length ?? 0;
  const seatLimit = plan === 'free' ? FREE_SEAT_LIMIT : null;
  const seatExceeded = seatLimit != null && seatCount >= seatLimit;
  const hasStripeCustomer = !!subscription.data?.stripeCustomerId;

  return (
    <div className="space-y-6">
      {billingBanner === 'success' && (
        <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800 flex items-center justify-between gap-2">
          <span>{t('settings.billingSuccess', { defaultValue: 'Subscription updated. It can take a moment for the new plan to reflect — refresh if needed.' })}</span>
          <button type="button" onClick={() => setBillingBanner(null)} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">×</button>
        </div>
      )}
      {billingBanner === 'cancelled' && (
        <div role="status" className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] text-ink-700 flex items-center justify-between gap-2">
          <span>{t('settings.billingCancelled', { defaultValue: 'Checkout cancelled. No changes were made.' })}</span>
          <button type="button" onClick={() => setBillingBanner(null)} className="text-ink-500 hover:text-ink-900 text-xs font-semibold">×</button>
        </div>
      )}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-ink-900">{t('settings.subscription', { defaultValue: 'Subscription' })}</h2>
              <PlanBadge plan={plan} />
            </div>
            <p className="mt-1 text-sm text-ink-600">
              {plan === 'free'
                ? t('settings.freePlanSummary', { defaultValue: 'Free plan — up to {{limit}} employees in one country.', limit: FREE_SEAT_LIMIT })
                : (subscription.data?.interval === 'yearly'
                    ? t('settings.paidPlanSummaryYearly', { defaultValue: '{{plan}} plan — billed yearly.', plan: PLAN_LABELS[plan] })
                    : t('settings.paidPlanSummaryMonthly', { defaultValue: '{{plan}} plan — billed monthly.', plan: PLAN_LABELS[plan] }))}
            </p>
            {plan !== 'free' && subscription.data?.currentPeriodEnd && (
              <p className="mt-1 text-xs text-ink-500">
                {subscription.data.cancelAtPeriodEnd
                  ? t('settings.cancelsOn', { defaultValue: 'Cancels on {{date}} — access continues until then.', date: fmtBillingDate(subscription.data.currentPeriodEnd) })
                  : t('settings.renewsOn', { defaultValue: 'Renews on {{date}}.', date: fmtBillingDate(subscription.data.currentPeriodEnd) })}
              </p>
            )}
          </div>
          {hasStripeCustomer && (
            <button
              type="button"
              onClick={() => portal.mutate(undefined)}
              disabled={portal.isPending}
              className="rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60"
            >
              {portal.isPending ? t('settings.opening', { defaultValue: 'Opening…' }) : t('settings.manageSubscription', { defaultValue: 'Manage subscription' })}
            </button>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <SeatChip seatCount={seatCount} seatLimit={seatLimit} exceeded={seatExceeded} />
          <Chip
            label={t('settings.countries', { defaultValue: 'Countries' })}
            value={`${(company.data?.countries ?? []).length}${plan === 'free' ? ' / 1' : ''}`}
            tone={plan === 'free' && (company.data?.countries ?? []).length > 1 ? 'warn' : 'neutral'}
          />
        </div>

        {portal.isError && (
          <p className="mt-3 text-xs text-rose-600">
            {(portal.error as Error)?.message ?? t('settings.portalError', { defaultValue: 'Could not open billing portal.' })}
          </p>
        )}

        {seatExceeded && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {t('settings.seatLimitReached', { defaultValue: "You've reached the Free-plan limit of {{limit}} employees. Upgrade to add more.", limit: FREE_SEAT_LIMIT })}
          </div>
        )}
      </div>

      {/* Plan picker — visible to free plans (so they can upgrade) and to
          paid plans (so they can switch tier or change cycle). Stripe
          Customer Portal *also* supports tier switches, but this gives a
          one-click affordance without leaving the app. */}
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-ink-900">
              {plan === 'free' ? t('settings.upgradePlan', { defaultValue: 'Upgrade plan' }) : t('settings.switchPlan', { defaultValue: 'Switch plan' })}
            </h2>
            <p className="mt-1 text-sm text-ink-600">
              {t('settings.perEmployeePricing', { defaultValue: 'Per-employee pricing. Seats auto-sync with your active employee count.' })}
            </p>
          </div>
          <IntervalToggle interval={interval} onChange={setInterval} />
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          {(['growth', 'global', 'enterprise'] as PaidTier[]).map((tier) => (
            <PlanPickCard
              key={tier}
              tier={tier}
              interval={interval}
              currentPlan={plan}
              currentInterval={subscription.data?.interval ?? null}
              busy={checkout.isPending}
              planInfo={plansQ.data?.find((p) => p.code === tier) ?? null}
              loading={plansQ.isLoading}
              onSubscribe={() =>
                checkout.mutate({ plan: tier, interval, seats: Math.max(seatCount, 1) })
              }
            />
          ))}
        </div>

        {checkout.isError && (
          <p className="mt-3 text-xs text-rose-600">
            {(checkout.error as Error)?.message ?? t('settings.checkoutError', { defaultValue: 'Could not start checkout.' })}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <h2 className="text-base font-semibold text-ink-900">{t('settings.whatEachPlanIncludes', { defaultValue: 'What each plan includes' })}</h2>
        <ul className="mt-3 space-y-2 text-sm text-ink-700">
          <li><strong>{t('settings.planFreeLabel', { defaultValue: 'Free:' })}</strong> {t('settings.planFreeFeatures', { defaultValue: 'Up to {{limit}} employees · one country · core HR.', limit: FREE_SEAT_LIMIT })}</li>
          <li><strong>{t('settings.planGrowthLabel', { defaultValue: 'Growth:' })}</strong> {t('settings.planGrowthFeatures', { defaultValue: 'Unlimited seats · e-signature · approvals · performance reviews.' })}</li>
          <li><strong>{t('settings.planGlobalLabel', { defaultValue: 'Global:' })}</strong> {t('settings.planGlobalFeatures', { defaultValue: 'Multi-country payroll (JP/US/UK/DE) · Border Buddy AI · audit log.' })}</li>
          <li><strong>{t('settings.planEnterpriseLabel', { defaultValue: 'Enterprise:' })}</strong> {t('settings.planEnterpriseFeatures', { defaultValue: 'SSO/SAML/SCIM · 7-year retention · dedicated support.' })}</li>
        </ul>
      </div>
    </div>
  );
}

function IntervalToggle({ interval, onChange }: { interval: StripeInterval; onChange: (i: StripeInterval) => void }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex rounded-lg border border-ink-200 bg-ink-50 p-0.5 text-xs font-medium">
      {(['monthly', 'yearly'] as StripeInterval[]).map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className={`px-3 py-1.5 rounded-md transition ${
            interval === i ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600 hover:text-ink-900'
          }`}
        >
          {i === 'monthly' ? t('settings.monthly', { defaultValue: 'Monthly' }) : t('settings.yearlySave', { defaultValue: 'Yearly · save 17%' })}
        </button>
      ))}
    </div>
  );
}

function PlanPickCard({
  tier, interval, currentPlan, currentInterval, busy, planInfo, loading, onSubscribe,
}: {
  tier: PaidTier;
  interval: StripeInterval;
  currentPlan: PlanCode;
  currentInterval: StripeInterval | null;
  busy: boolean;
  /** Stripe-sourced live price for the tier (null while loading). */
  planInfo: PlanCatalogEntry | null;
  loading: boolean;
  onSubscribe: () => void;
}) {
  const { t } = useTranslation();
  // Prices come from the backend (which reads them live from Stripe).
  // We never hardcode amounts here — that would silently drift from
  // whatever Checkout actually charges.
  const cents = !planInfo ? 0 : (interval === 'monthly' ? planInfo.unitPriceMonthlyCents : planInfo.unitPriceYearlyCents);
  const currency = (planInfo?.currency ?? 'usd').toUpperCase();
  const priceLabel = loading || !planInfo
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  const isCurrent = currentPlan === tier && currentInterval === interval;
  const label = PLAN_LABELS[tier];
  return (
    <div className={`rounded-xl border p-4 ${isCurrent ? 'border-accent-500 bg-accent-50' : 'border-ink-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-ink-900">{label}</span>
        {isCurrent && <span className="text-[10px] uppercase tracking-wide font-bold text-accent-700">{t('settings.current', { defaultValue: 'Current' })}</span>}
      </div>
      <p className="mt-2 num text-2xl font-bold text-ink-900">{priceLabel}</p>
      <p className="text-xs text-ink-500">
        {interval === 'monthly'
          ? t('settings.perEmployeePerMonth', { defaultValue: '/ employee / month' })
          : t('settings.perEmployeePerYear', { defaultValue: '/ employee / year' })}
      </p>
      <button
        type="button"
        disabled={busy || isCurrent}
        onClick={onSubscribe}
        className="mt-4 w-full rounded-md bg-accent-600 px-3 py-2 text-xs font-semibold text-white hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCurrent ? t('settings.active', { defaultValue: 'Active' }) : busy ? t('settings.opening', { defaultValue: 'Opening…' }) : currentPlan === 'free' ? t('settings.subscribe', { defaultValue: 'Subscribe' }) : t('settings.switch', { defaultValue: 'Switch' })}
      </button>
    </div>
  );
}

function SeatChip({ seatCount, seatLimit, exceeded }: { seatCount: number; seatLimit: number | null; exceeded: boolean }) {
  const { t } = useTranslation();
  const value = seatLimit != null ? `${seatCount} / ${seatLimit}` : `${seatCount}`;
  return <Chip label={t('settings.seats', { defaultValue: 'Seats' })} value={value} tone={exceeded ? 'warn' : 'neutral'} />;
}

function Chip({ label, value, tone }: { label: string; value: string; tone: 'neutral' | 'warn' }) {
  const cls = tone === 'warn' ? 'bg-amber-100 text-amber-800' : 'bg-ink-100 text-ink-700';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
      <span className="opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

function AuditLogTab() {
  const { t } = useTranslation();
  const events = useAuditLog({ limit: 200 });
  return (
    <div className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-ink-100 bg-ink-50">
        <h2 className="text-sm font-semibold text-ink-700">{t('settings.auditLog', { defaultValue: 'Audit log' })}</h2>
        <p className="text-[10px] text-ink-500">{t('settings.auditLogDesc', { defaultValue: 'Every PII reveal, payroll run, approval, employee change, AI run — logged with IP + user-agent.' })}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-white text-ink-500">
            <tr>
              <th className="text-start font-medium px-3 py-2">{t('settings.auditAction', { defaultValue: 'Action' })}</th>
              <th className="text-start font-medium px-3 py-2">{t('settings.auditTarget', { defaultValue: 'Target' })}</th>
              <th className="text-start font-medium px-3 py-2">{t('settings.auditUser', { defaultValue: 'User' })}</th>
              <th className="text-start font-medium px-3 py-2">{t('settings.auditIp', { defaultValue: 'IP' })}</th>
              <th className="text-start font-medium px-3 py-2">{t('settings.auditWhen', { defaultValue: 'When' })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {events.isLoading && <tr><td colSpan={5} className="text-center text-ink-500 py-6">{t('settings.loading', { defaultValue: 'Loading…' })}</td></tr>}
            {events.data?.length === 0 && <tr><td colSpan={5} className="text-center text-ink-500 py-6">{t('settings.noAuditEvents', { defaultValue: 'No audit events yet.' })}</td></tr>}
            {(events.data ?? []).map((ev) => (
              <tr key={ev.id} className={`hover:bg-ink-50/40 ${ev.action.startsWith('pii.reveal') ? 'bg-warn-50/30' : ''}`}>
                <td className="px-3 py-2 font-mono text-ink-900">{ev.action}</td>
                <td className="px-3 py-2 text-ink-700">{ev.targetKind ? `${ev.targetKind}:${(ev.targetId ?? '').slice(0, 8)}…` : '—'}</td>
                <td className="px-3 py-2 font-mono text-ink-500">{ev.userId ? ev.userId.slice(0, 8) : '—'}</td>
                <td className="px-3 py-2 text-ink-500">{ev.ip ?? '—'}</td>
                <td className="px-3 py-2 text-ink-500">{new Date(ev.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="border-b border-ink-100 pb-2">
      <dt className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</dt>
      <dd className="text-sm text-ink-900 mt-0.5">{value ?? '—'}</dd>
    </div>
  );
}

function FieldNode({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-ink-100 pb-2">
      <dt className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}
